
import { useCallback, useEffect, useRef, useState } from 'react';
// Legacy API polling fallback intentionally retained below for reference.
// import { addUserActivityStatus } from '../../services/actions/chat/activityStatusActions';
import { useCustomDispatch } from '../../hooks';
import { authToken } from '../../storage/authToken';
import { useCustomSelectors } from '../../services/selectors/allSelectors';
import { getRealtimeSocket, subscribeRealtimeEvent, subscribeRealtimeStatus } from '../../services/realtime/realtimeSocket';
import {
	notifyPresenceActive,
	notifyPresenceConnect,
	notifyPresenceDisconnect,
	notifyPresenceHeartbeat,
	notifyPresenceIdle,
} from '../../services/realtime/pharmacyPresence';
import { useCustomLocation } from '../../hooks';
import { fetchAllRecentChats } from '../../services/actions/chat/recentChatsListsActions';
import { isPortalChatOpen } from '../../services/chat/chatRefreshGate';

const IDLE_TIMEOUT_MS = 5 * 60 * 1000;
const HEARTBEAT_INTERVAL_MS = 60 * 1000;
const ACTIVE_EMIT_THROTTLE_MS = 30 * 1000;

const ActivityTracker = () => {

	const idleTimerRef = useRef(null);
	const statusRef = useRef('offline');
	const connectionIdRef = useRef(null);
	const lastActiveEmitRef = useRef(0);

	const dispatch = useCustomDispatch()
	const { pharmacyId } = useCustomSelectors()
	const location = useCustomLocation()
	const token = authToken()

	const [, setActivityStatus] = useState('online');
	const [connectionId, setConnectionId] = useState(null);

	const setPresenceStatus = useCallback((status) => {
		statusRef.current = status;
		setActivityStatus(status);
	}, []);

	const sendOnlineActivity = useCallback((force = false) => {
		const activeConnectionId = connectionIdRef.current;
		if (!token || !activeConnectionId) return;

		const now = Date.now();
		const wasIdle = statusRef.current === 'idle';
		if (!force && !wasIdle && now - lastActiveEmitRef.current < ACTIVE_EMIT_THROTTLE_MS) {
			return;
		}

		lastActiveEmitRef.current = now;
		setPresenceStatus('online');
		notifyPresenceActive(activeConnectionId).catch(() => {});
	}, [setPresenceStatus, token]);

	const resetIdleTimer = useCallback(() => {
		clearTimeout(idleTimerRef.current);
		idleTimerRef.current = setTimeout(() => {
			if (!token || !connectionIdRef.current || statusRef.current === 'idle') return;

			setPresenceStatus('idle');
			notifyPresenceIdle(connectionIdRef.current).catch(() => {});
		}, IDLE_TIMEOUT_MS)
	}, [setPresenceStatus, token]);

	const handleActivity = useCallback(() => {
		sendOnlineActivity(statusRef.current === 'idle');
		resetIdleTimer();
	}, [resetIdleTimer, sendOnlineActivity]);

	useEffect(() => {
		const handleRealtimeStatus = (status) => {
			if (status?.status === 'connected' && status.socketId) {
				connectionIdRef.current = status.socketId;
				setConnectionId(status.socketId);
				setPresenceStatus('online');
				lastActiveEmitRef.current = Date.now();
				notifyPresenceConnect(status.socketId).catch(() => {});
				resetIdleTimer();
			}

			if (status?.status === 'disconnected') {
				const disconnectedConnectionId = status.socketId || connectionIdRef.current;
				if (disconnectedConnectionId) {
					notifyPresenceDisconnect(disconnectedConnectionId).catch(() => {});
				}
				connectionIdRef.current = null;
				setConnectionId(null);
				setPresenceStatus('offline');
				clearTimeout(idleTimerRef.current);
			}
		};

		const subscription = subscribeRealtimeStatus(handleRealtimeStatus);
		const realtimeSocket = getRealtimeSocket();
		if (realtimeSocket?.connected) {
			handleRealtimeStatus({ status: 'connected', socketId: realtimeSocket.id });
		}

		return () => {
			subscription.close();
		};
	}, [resetIdleTimer, setPresenceStatus]);

	useEffect(() => {
		window.addEventListener('mousemove', handleActivity);
		window.addEventListener('mousedown', handleActivity);
		window.addEventListener('click', handleActivity);
		window.addEventListener('keydown', handleActivity);
		window.addEventListener('input', handleActivity);
		window.addEventListener('scroll', handleActivity, { passive: true });

		resetIdleTimer();

		return () => {
			window.removeEventListener('mousemove', handleActivity);
			window.removeEventListener('mousedown', handleActivity);
			window.removeEventListener('click', handleActivity);
			window.removeEventListener('keydown', handleActivity);
			window.removeEventListener('input', handleActivity);
			window.removeEventListener('scroll', handleActivity);
			clearTimeout(idleTimerRef.current);
		};
	}, [handleActivity, resetIdleTimer]);

	useEffect(() => {
		handleActivity();
	}, [handleActivity, location.pathname, location.search]);

	useEffect(() => {
		if (!token || !connectionId) return undefined;

		const heartbeatId = setInterval(() => {
			notifyPresenceHeartbeat(connectionId).catch(() => {});
		}, HEARTBEAT_INTERVAL_MS);

		return () => clearInterval(heartbeatId);
	}, [connectionId, token]);

	useEffect(() => {
		const disconnectCurrentConnection = () => {
			if (connectionIdRef.current) {
				notifyPresenceDisconnect(connectionIdRef.current, { keepalive: true });
			}
		};

		window.addEventListener('beforeunload', disconnectCurrentConnection);
		window.addEventListener('pagehide', disconnectCurrentConnection);

		return () => {
			window.removeEventListener('beforeunload', disconnectCurrentConnection);
			window.removeEventListener('pagehide', disconnectCurrentConnection);
			disconnectCurrentConnection();
		};
	}, []);

	// Legacy API polling fallback, disabled in favor of authenticated socket-driven presence.
	// Keep this block for future rollback/reference; do not enable it together with socket presence.
	// useEffect(() => {
	// 	const activityInterval = {
	// 		online: 1 * 60 * 1000,
	// 		idle: 2 * 60 * 1000,
	// 		offline: 5 * 60 * 1000,
	// 	};
	//
	// 	if (token && activityStatus) {
	// 		dispatch(addUserActivityStatus(activityStatus));
	// 	}
	//
	// 	const intervalId = setInterval(() => {
	// 		if (token && activityStatus) {
	// 			dispatch(addUserActivityStatus(activityStatus));
	// 		}
	// 	}, activityInterval[activityStatus]);
	//
	// 	return () => clearInterval(intervalId);
	// }, [activityStatus, dispatch, token]);


	useEffect(() => {
		if (!token || !pharmacyId) return undefined;

		const subscription = subscribeRealtimeEvent('pharmacy-user-presence-updated', ({ data }) => {
			if (isPortalChatOpen() && Number(data?.pharmacy_id) === Number(pharmacyId)) {
				dispatch(fetchAllRecentChats());
			}
		});

		return () => {
			subscription.close();
		};

	}, [dispatch, pharmacyId, token])

	return null

};

export default ActivityTracker



